import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import AssessmentForm from '@/components/AssessmentForm.jsx';
import {
  Search, ShieldCheck, Code, AlertTriangle, Wrench, Layout, GraduationCap, Briefcase,
  ArrowRight, ArrowUpRight, Clock, MessageSquareText, Layers, Sparkles, Users, Compass, HeartHandshake,
  ExternalLink
} from 'lucide-react';

function HomePage() {
  const services = [
    { title: 'Diagnóstico inicial', desc: 'Escaneo rápido de tu superficie de ataque para saber qué está expuesto.', icon: Search, tag: '01' },
    { title: 'Auditoría integral', desc: 'Revisión profunda de sistemas, redes y procesos contra buenas prácticas.', icon: ShieldCheck, tag: '02' },
    { title: 'Pentesting', desc: 'Simulamos ataques reales y controlados para validar tus defensas.', icon: Code, tag: '03' },
    { title: 'Respuesta a incidentes', desc: 'Asistencia inmediata si algo sale mal, para contener y recuperar.', icon: AlertTriangle, tag: '04' },
    { title: 'Implementación', desc: 'Seleccionamos e instalamos las herramientas correctas para tu caso.', icon: Wrench, tag: '05' },
    { title: 'Arquitectura y consultoría', desc: 'Diseñamos una estrategia de seguridad escalable y por diseño.', icon: Layout, tag: '06' },
    { title: 'Concientización', desc: 'Entrenamos a tu equipo y creamos políticas claras y útiles.', icon: GraduationCap, tag: '07' },
    { title: 'Gestión de riesgos y vCISO', desc: 'Dirección estratégica de seguridad sin el costo de un cargo full-time.', icon: Briefcase, tag: '08' },
  ];

  const principles = [
    { icon: MessageSquareText, title: 'Atención personalizada', text: 'Trabajamos con tu empresa, no con tickets anónimos.' },
    { icon: Layers, title: 'Lenguaje claro', text: 'Informes accionables, sin tecnicismos innecesarios.' },
    { icon: Clock, title: 'Respuesta rápida', text: 'Te respondemos en menos de 24 horas hábiles.' },
    { icon: Sparkles, title: 'Seguridad por diseño', text: 'Protegemos desde el inicio, no después del problema.' },
  ];

  const methodology = [
    { number: '01', title: 'Compartís tu web, dominio o API', description: 'Iniciamos con la información básica de tus activos digitales para entender tu superficie de exposición pública.' },
    { number: '02', title: 'Nos contás tu empresa y necesidad', description: 'Entendemos el contexto de tu negocio, las normativas que te aplican y los desafíos específicos que enfrentás.' },
    { number: '03', title: 'Recibís una primera evaluación', description: 'Te devolvemos un informe claro, con recomendaciones accionables y priorizadas. Sin compromiso.' },
  ];

  return (
    <>
      <Helmet>
        <title>Faronexo | Consultoría de Ciberseguridad para Pymes</title>
        <meta name="description" content="Faronexo es una consultoría de ciberseguridad nueva, impulsada por un equipo con trayectoria. Diagnósticos y soluciones claras y adaptadas a pymes. Solicitá tu evaluación gratuita." />
      </Helmet>

      <a href="#contenido" className="fx-skip">Saltar al contenido</a>
      <Header />

      <main id="contenido" className="bg-background">
        {/* Hero */}
        <section className="fx-hero">
          <div className="fx-container">
            <div className="fx-hero-grid">
              <motion.div
                className="fx-hero-copy"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="fx-eyebrow">
                  <span className="fx-dot" /> Consultoría de ciberseguridad
                </span>
                <h1>
                  Seguridad que tu empresa puede <span>entender y aplicar</span>
                </h1>
                <p className="fx-intro">
                  Atención personalizada para pymes que quieren mejorar su postura de ciberseguridad desde el inicio. Detrás de Faronexo hay un equipo con trayectoria en proteger lo que más importa.
                </p>
                <div className="fx-actions">
                  <a href="#evaluacion" className="fx-button">
                    Solicitar evaluación rápida
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/servicios" className="fx-text-link text-muted-foreground">
                    Ver servicios
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="fx-hero-notes">
                  <span><Clock className="w-4 h-4" /> Respuesta en 24h hábiles</span>
                  <span><ShieldCheck className="w-4 h-4" /> Sin compromiso inicial</span>
                  <span><MessageSquareText className="w-4 h-4" /> Lenguaje claro</span>
                </div>
              </motion.div>

              <motion.div
                className="fx-hero-visual"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                <img
                  src="/assets/faronexo-hero.webp"
                  alt="Faro de ciberseguridad emitiendo señales sobre un mar de datos"
                />
                <div className="fx-image-shade" />
                <div className="fx-image-tag">
                  <Sparkles className="w-3.5 h-3.5" />
                  Monitoreo y diagnóstico activo
                </div>
                <div className="fx-image-caption">
                  <span>Propuesta de valor</span>
                  <p>Detectamos lo que otros pasan por alto</p>
                  <Link to="/servicios" aria-label="Conocer los servicios">
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Principles band */}
            <div className="fx-principles">
              {principles.map((p) => (
                <span key={p.title}>
                  <p.icon className="w-5 h-5 mb-2" style={{ color: '#ffbf50' }} />
                  <strong>{p.title}</strong>
                  <br />
                  {p.text}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="fx-section fx-services">
          <div className="fx-container">
            <div className="fx-section-head">
              <div>
                <span className="fx-eyebrow"><span className="fx-dot" /> Qué hacemos</span>
                <h2>Servicios que protegen <span>lo que importa</span></h2>
              </div>
              <div>
                <p>Soluciones claras y directas para proteger la información de tu empresa contra amenazas modernas, adaptadas a tu presupuesto y realidad.</p>
                <Link to="/servicios" className="fx-text-link">
                  Ver todos los servicios <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="fx-service-grid">
              {services.map((s, i) => (
                <motion.article
                  key={s.title}
                  className="fx-service"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                >
                  <div className="fx-service-top">
                    <s.icon className="w-6 h-6" />
                    <span>{s.tag}</span>
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <Link to="/servicios" className="fx-service-arrow" aria-label={`Conocer más sobre ${s.title}`}>
                    <ArrowUpRight className="w-5 h-5" />
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Trust: new company, experienced team */}
        <section className="fx-section">
          <div className="fx-container">
            <div className="fx-method-grid">
              <motion.div
                initial={{ opacity: 0, x: -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="fx-eyebrow"><span className="fx-dot" /> Quiénes somos</span>
                <h2>
                  Una empresa nueva, <span>un equipo con recorrido</span>
                </h2>
                <p className="fx-method-intro">
                  Faronexo nace como un proyecto joven, pero lo impulsan profesionales que ya vienen trabajando en ciberseguridad. Combinamos la cercanía y el compromiso de una consultoría nueva con la experiencia práctica de un equipo que conoce los riesgos reales que enfrentan las pymes.
                </p>
                <p className="fx-method-intro" style={{ marginTop: '18px' }}>
                  Sin promesas vacías ni números inflados: lo que ofrecemos es atención honesta, mirada técnica y el compromiso de acompañar cada proyecto de cerca.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                <div className="fx-steps" style={{ marginBlock: '0' }}>
                  {[
                    { icon: Users, title: 'Trayectoria previa en ciberseguridad', text: 'El equipo llega con experiencia práctica en diagnóstico, auditoría y respuesta a incidentes.' },
                    { icon: Compass, title: 'Mirada pensada para pymes', text: 'Entendemos la realidad de empresas que no cuentan con un equipo de seguridad dedicado.' },
                    { icon: HeartHandshake, title: 'Compromiso con cada cliente', text: 'Al ser una empresa nueva, cada proyecto nos importa especialmente. Te acompañamos de cerca.' },
                  ].map((item) => (
                    <div className="fx-step" key={item.title}>
                      <span><item.icon className="w-5 h-5" style={{ color: '#ffbf50' }} /></span>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Current clients */}
        <section className="fx-section" aria-labelledby="clientes-title">
          <div className="fx-container">
            <div className="fx-section-head">
              <div>
                <span className="fx-eyebrow"><span className="fx-dot" /> Experiencia actual</span>
                <h2 id="clientes-title">Clientes con los que <span>trabajamos hoy</span></h2>
              </div>
              <p>Actualmente acompañamos a estas organizaciones. Conocé más sobre cada una en su sitio oficial.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="https://osanasalud.com/"
                target="_blank"
                rel="noreferrer"
                className="group flex min-h-[170px] flex-col justify-between rounded-[9px] border border-white/10 bg-[#152032] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-[#ffbf50]/50 hover:bg-[#1c293e]"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="grid h-12 w-12 place-items-center rounded-md border border-[#ffbf50]/30 bg-[#ffbf50]/10 text-lg font-semibold tracking-tight text-[#ffcf7b]" aria-hidden="true">O</span>
                  <ExternalLink className="h-5 w-5 text-[#9ba8bb] transition-colors group-hover:text-[#ffbf50]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">Osana</h3>
                  <p className="mt-1 text-sm text-[#aeb9c9]">Soluciones de salud digital</p>
                </div>
              </a>

              <a
                href="https://sitio.sg.com.ar/"
                target="_blank"
                rel="noreferrer"
                className="group flex min-h-[170px] flex-col justify-between rounded-[9px] border border-white/10 bg-[#152032] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-[#ffbf50]/50 hover:bg-[#1c293e]"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="grid h-12 w-12 place-items-center rounded-md border border-[#ffbf50]/30 bg-[#ffbf50]/10 text-lg font-semibold tracking-tight text-[#ffcf7b]" aria-hidden="true">SG</span>
                  <ExternalLink className="h-5 w-5 text-[#9ba8bb] transition-colors group-hover:text-[#ffbf50]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">Sanatorio Güemes</h3>
                  <p className="mt-1 text-sm text-[#aeb9c9]">Institución de salud</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Methodology + Assessment */}
        <section className="fx-section">
          <div className="fx-container">
            <div className="fx-method-grid">
              <motion.div
                initial={{ opacity: 0, x: -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="fx-eyebrow"><span className="fx-dot" /> Cómo trabajamos</span>
                <h2>
                  Un proceso <span>simple y transparente</span>
                </h2>
                <p className="fx-method-intro">
                  Diseñado para no interrumpir la operación de tu negocio mientras mejoramos tu postura de seguridad.
                </p>

                <div className="fx-steps">
                  {methodology.map((step) => (
                    <div className="fx-step" key={step.number}>
                      <span>{step.number}</span>
                      <div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                id="evaluacion"
                className="fx-assessment scroll-mt-24"
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                <AssessmentForm />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="fx-final">
          <div className="fx-container">
            <div>
              <span className="fx-eyebrow"><span className="fx-dot" /> Primer paso</span>
              <h2>La seguridad no tiene que ser complicada</h2>
              <p>Da el primer paso hacia una empresa más segura. Hablemos sobre tus necesidades y encontremos la solución adecuada.</p>
            </div>
            <Link to="/solicitar-evaluacion" className="fx-button">
              Solicitar evaluación
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default HomePage;
